import { useEffect } from "react"
import { useStore } from "zustand"
import { createStore } from "zustand/vanilla"

import { getDefaultThemeId, type ThemeId } from "@/styles/themes"
import { DEFAULT_THEME_ID } from "@/styles/themes/seeds"

export type ResolvedTheme = "dark" | "light"
type ThemeSource = "class" | "system"

type ThemeState = {
  resolvedTheme: ResolvedTheme
  isDark: boolean
  source: ThemeSource
  themeId: ThemeId
  themeCatalogVersion: number
}

const DARK_QUERY = "(prefers-color-scheme: dark)"

const getThemeStateSnapshot = (): ThemeState => {
  let themeId: ThemeId = DEFAULT_THEME_ID
  try {
    themeId = getDefaultThemeId()
  } catch (e) {
    if (!(e instanceof ReferenceError)) throw e
  }

  if (typeof document === "undefined" || typeof window === "undefined") {
    return {
      resolvedTheme: "dark",
      isDark: true,
      source: "class",
      themeId,
      themeCatalogVersion: 0,
    }
  }

  const root = document.documentElement
  if (root.classList.contains("dark")) {
    return {
      resolvedTheme: "dark",
      isDark: true,
      source: "class",
      themeId,
      themeCatalogVersion: 0,
    }
  }

  if (root.classList.contains("light")) {
    return {
      resolvedTheme: "light",
      isDark: false,
      source: "class",
      themeId,
      themeCatalogVersion: 0,
    }
  }

  const isDark = window.matchMedia(DARK_QUERY).matches
  return {
    resolvedTheme: isDark ? "dark" : "light",
    isDark,
    source: "system",
    themeId,
    themeCatalogVersion: 0,
  }
}

export const themeStore = createStore<ThemeState>(getThemeStateSnapshot)

let observerAttached = false

export const ensureThemeObserver = (): void => {
  if (
    observerAttached ||
    typeof document === "undefined" ||
    typeof window === "undefined"
  ) {
    return
  }

  observerAttached = true

  const root = document.documentElement
  const media = window.matchMedia(DARK_QUERY)
  const syncTheme = () => {
    const prev = themeStore.getState().themeCatalogVersion
    themeStore.setState({
      ...getThemeStateSnapshot(),
      themeCatalogVersion: prev,
    })
  }

  const observer = new MutationObserver(syncTheme)
  observer.observe(root, {
    attributes: true,
    attributeFilter: ["class"],
  })

  media.addEventListener("change", syncTheme)
  syncTheme()
}

export const useResolvedTheme = (): ResolvedTheme => {
  useEffect(() => {
    ensureThemeObserver()
  }, [])

  return useStore(themeStore, (state) => state.resolvedTheme)
}

export const useIsDarkTheme = (): boolean => {
  useEffect(() => {
    ensureThemeObserver()
  }, [])

  return useStore(themeStore, (state) => state.isDark)
}

export const useActiveThemeId = (): ThemeId => {
  useEffect(() => {
    ensureThemeObserver()
  }, [])

  return useStore(themeStore, (state) => state.themeId)
}

export const useThemeCatalogVersion = (): number => {
  useEffect(() => {
    ensureThemeObserver()
  }, [])

  return useStore(themeStore, (state) => state.themeCatalogVersion)
}
